import { defineStore } from 'pinia';
import type { CategoryList, CategoryItem, CkptType } from '@/types/model';
import { UNIVERSAL_RENDER_STYLE_CATEGORY_NAME, UNIVERSAL_RENDER_LEVEL2_ID } from '@/views/model/constants/publish';
import { request } from '@/axios';

// 获取模型分类信息
function queryCategory() {
  return request<CategoryList>({
    api: '/external/labelInfo',
    method: 'get',
    notToast: false
  });
}

function queryCategoryByManager() {
  return request<CategoryList>({
    api: '/external/getModelLabelInfoByManager',
    method: 'get',
    notToast: false
  });
}

/** 根据 level-2 标签 ID 查询标签信息（万能渲染固定 ID=88） */
function queryLabelInfoByLevel2Id(id: number) {
  return request<CategoryItem>({
    api: `/external/getModelLabelInfoByLevel2Id/${id}`,
    method: 'get',
    notToast: false
  });
}

export const useModelStore = defineStore('model', {
  state: () => ({
    category: [] as CategoryList,
    categoryByManager: [] as CategoryList,
    /** 万能渲染风格标签（来自 getModelLabelInfoByLevel2Id/88） */
    universalRenderLabels: null as CategoryItem | null,
    open: false,
    activeModelIndex: 0,
    activeKeywords: '',
    activeModelId: undefined as number | undefined,
    activeLoraType: undefined as 'LoRA' | 'Style' | 'Controller' | 'myself' | 'teamShareModel' | undefined,
    activeCkptType: undefined as string | undefined,
    activeCategory: undefined as number | undefined,
    activeLabel: undefined as number | undefined,
    firshIsController: false,
    ignore: false,
    modelFilterState: {
      keyworks: '',
      ckptType: undefined,
      loraType: undefined,
      category: undefined as number | undefined,
      label: undefined as number | undefined
    },
    modelScrollTop: 0,
    searchResult: '',
    isNeedRestSearch: true
  }),
  getters: {
    categoryOptions({ category }): Options<number> {
      return category.map(item => {
        return {
          label: item.name,
          value: item.id
        }
      });
    },
    labelOptions({ category }): Options {
      let labelOptions: Options = [];

      category.map(item => {
        labelOptions = labelOptions.concat((item.subList || []).map(subItem => {
          return {
            label: subItem.name,
            value: subItem.id
          }
        }))
      });

      // 去重
      let _labelOptions: Options = [];
      labelOptions.map(item => {
        const index = _labelOptions.findIndex(labelItem => labelItem.value === item.value);

        if (index === -1) {
          _labelOptions.push(item);
        }
      });

      return _labelOptions;
    }
  },
  actions: {
    async initModel() {
      const { data } = await queryCategory();
      this.category = data;
    },
    async initModelByManager() {
      const { data } = await queryCategoryByManager();
      this.categoryByManager = data;
    },
    async initUniversalRenderLabels() {
      if (this.universalRenderLabels) return;
      const { data } = await queryLabelInfoByLevel2Id(UNIVERSAL_RENDER_LEVEL2_ID);
      this.universalRenderLabels = data;
    },
    // 获取当前分类下的标签
    getCategoryLabel(categoryId?: number) {
      if (!categoryId) {
        return this.labelOptions;
      }

      const [target] = this.category.filter(item => item.id === categoryId);
      return (target.subList || []).map(subItem => {
        return {
          label: subItem.name,
          value: subItem.id
        }
      });
    },
    /** 获取标签列表，排除指定分类名称下的标签 */
    getCategoryLabelExcluding(excludeName: string): Options {
      let labelOptions: Options = [];

      this.category
        .filter(item => item.name !== excludeName)
        .map(item => {
          labelOptions = labelOptions.concat((item.subList || []).map(subItem => {
            return {
              label: subItem.name,
              value: subItem.id
            }
          }));
        });

      // 去重
      const seen = new Set();
      return labelOptions.filter(item => {
        if (seen.has(item.value)) return false;
        seen.add(item.value);
        return true;
      });
    },
    getCategoryLabelByManager(categoryId?: number) {
      if (!categoryId) {
        return this.labelOptions;
      }

      const [target] = this.categoryByManager.filter(item => item.id === categoryId);
      return (target.subList || []).map(subItem => {
        return {
          label: subItem.name,
          value: subItem.id
        }
      });
    },
    /** 万能渲染风格 Tag（优先使用 getModelLabelInfoByLevel2Id 接口数据） */
    getUniversalRenderStyleLabelOptions(): Options<number> {
      if (this.universalRenderLabels) {
        return (this.universalRenderLabels.subList ?? []).map((sub) => ({
          label: sub.name,
          value: sub.id,
        }));
      }
      // 兜底：依次从 categoryByManager、category（labelInfo）中按名称匹配
      const source = this.categoryByManager.length ? this.categoryByManager : this.category;
      const [root] = source.filter(
        (item) => item.name === UNIVERSAL_RENDER_STYLE_CATEGORY_NAME
      );
      return (root?.subList ?? []).map((sub) => ({
        label: sub.name,
        value: sub.id,
      }));
    },
    getCategoryText(value?: number) {
      const [target] = this.categoryOptions.filter(item => item.value === value);
      return target?.label || '全部';
    },
    getLabelText(value?: number) {
      const [target] = this.labelOptions.filter(item => item.value === value);
      return target?.label || '全部';
    },
    resetModalState() {
      this.activeModelIndex = 0;
      this.activeKeywords = '';
      this.activeModelId = undefined;
      this.activeLoraType = undefined;
      this.activeCkptType = undefined;
      this.activeCategory = undefined;
      this.activeLabel = undefined;
      this.firshIsController = false;
      this.ignore = false;
    }
  },
  persist: {
    key: 'pinoModelStore',
    storage: window.localStorage,
    // 不持久化标签树：后端调整分类后应立即生效，避免 localStorage 旧数据短路接口请求
    paths: [
      'open',
      'activeModelIndex',
      'activeKeywords',
      'activeModelId',
      'activeLoraType',
      'activeCkptType',
      'activeCategory',
      'activeLabel',
      'firshIsController',
      'ignore',
      'modelFilterState',
      'modelScrollTop',
      'searchResult',
      'isNeedRestSearch',
    ],
    // 兜底清除历史版本写入的标签树，防止插件/旧数据仍把它们灌回 store
    afterRestore: (ctx) => {
      ctx.store.$patch({
        category: [],
        categoryByManager: [],
        universalRenderLabels: null,
      });
    },
  },
});
